import React, { useState, useRef, useEffect, createRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog } from "@progress/kendo-react-dialogs";
import { FloatingActionButton, Button } from "@progress/kendo-react-buttons";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { getNotifications, sendEmail, sendNotification } from '../../actions/index.js';
import { Editor, EditorTools } from "@progress/kendo-react-editor";
import { Upload } from "@progress/kendo-react-upload";
import { process } from '@progress/kendo-data-query';
import EditAboutForm from '../../components/EditAboutForm';
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { Card, CardBody, CardHeader, CardSubtitle, CardTitle } from '@progress/kendo-react-layout';
import Tooltip from '@mui/material/Tooltip';
import { formCloser } from '../../utils/formCloser.js';
import { Input } from '@mui/material';
import { getEmails } from '../../actions/portal/email/getEmails.js';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { CheckmarkCell } from '../../components/GridCell/CheckmarkCell.jsx';
import NotificationListView from '../../components/NotificationListView.jsx';


const {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Indent,  
  Undo,
  Redo,
  FontSize,
  FontName,
  Link,
  Unlink,
  InsertImage,
} = EditorTools

const initialDataState = {
  skip: 0,
  take: 10,
}

const DashboardNotifications = () => {
  const { token, user } = useSelector(state => state.authReducer);
  const { users } = useSelector(state => state.usersReducer);
  const { groups } = useSelector(state => state.groupsReducer);
  const { emails } = useSelector(state => state.emailReducer);
  const { adminNotifications } = useSelector(state => state.adminNotificationReducer);
  const [dialogProps, setDialogProps] = useState({});
  const [dataState, setDataState] = useState(initialDataState);
  const [to, setTo] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [subject, setSubject] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [emailBody, setEmailBody] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [targetNotification, setTargetNotification] = useState({});
  const [showListView, setShowListView] = useState(false);

  const handleStatClick = (dataItem) => {
    setTargetNotification(dataItem);
    setShowListView(true);
  }

  const _exporter = createRef();

  const excelExport = () => {
      if (_exporter.current) {
          _exporter.current.save();
      }
  };

  const onChange = (event) => {
    setEmailBody(event?.target?.value?.textContent);
  }
  
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getEmails(token));
    dispatch(getNotifications(token));
  }, []);

  useEffect(() => {
    setFilteredNotifications(preProcess(adminNotifications, dataState));
  }, [adminNotifications])
  
  const preProcess = () => {
    let processedNotifications = [];

    adminNotifications.forEach((notif, idx) => {
      processedNotifications.push( { 
        ...notif, 
        toCount: JSON.parse(notif.toList).length,
      })
    })

    return processedNotifications;
  }

  const clearNotificationFields = () => {
    setTo([]);
    setSubject('');
    setEmailBody('');
  }

  const notificationSubmit = () => {
    dispatch(sendNotification(to, emailBody, subject, token, clearNotificationFields))
  }

  return (
    <>
      <div style={{ width: '100%', minHeight: '50px' }}>
        <FloatingActionButton
          text={showNotifications ? 'View Notification Builder' : 'View Notification Statistics'}
          onClick={() => setShowNotifications(!showNotifications)}
          align={{ horizontal: 'start', vertical: 'top' }}
          alignOffset={{ x: '30px' }}
          shape='rectangle'
          positionMode='absolute'
          style={{ zIndex: 1000, minWidth: '150px', padding: '10px 20px' }}
        />
        {
          showNotifications && 
          <>
            <Input 
              onChange={(e) => {
                  setFilteredNotifications(preProcess(adminNotifications, dataState).filter((notif) => {
                    return notif.notificationSubject.toLowerCase().includes(e.target.value.toLowerCase())
                  }
                ))
              }}
              placeholder="Search" 
              sx={{ left: '250px', top: '10px' }}
            />
            <Button type="button" style={{ float: 'right' }} onClick={excelExport}>
              Export to Excel
            </Button>
            <ExcelExport
              fileName={"Notification Statistics: " + new Date().toLocaleDateString()}
              data={filteredNotifications}
              ref={_exporter}>
                <ExcelExportColumn field="notificationSubject" title="Subject" />
                <ExcelExportColumn field="toCount" title="To" />
                <ExcelExportColumn field="sentBy" title="Sent By" />
                <ExcelExportColumn field="dateSent" title="Time Sent" />
            </ExcelExport>
          </>
        }
      </div>
      {!showNotifications ?
        <Card style={styles.card}>
          <CardHeader className='k-hbox' style={styles.cardHeader}>
            <CardTitle style={styles.cardTitle}>Notifications</CardTitle>
            <CardSubtitle style={styles.cardSubtitle}>
              To:
              <MultiSelect
                style={{ 
                  width: '80%',
                  paddingLeft: '20px',
                  paddingBottom: '20px',
                  marginRight: '17%'
                }}
                value={to}
                onChange={(e) => setTo(e.value)}
                data={groups.concat(users.map(u => {return { name: u.email, id: u.id, isUser: true }}))}
                textField="name"
                dataItemKey="id"/>
              <div style={{ display: 'inline', paddingRight: '20px' }}>Subject:</div>
              <Input style={{ width: '77%' }} value={subject} onChange={(e) => setSubject(e.target.value)}/>
            </CardSubtitle>
          </CardHeader>
          <CardBody>      
            <Editor 
              value={emailBody}
              onChange={onChange}
            />

            { !user.viewOnly && <FloatingActionButton  
              onClick={notificationSubmit}
              text='Send Notification'
              align={{ horizontal: 'end', vertical: 'top' }}
              shape='rectangle'
              positionMode='absolute'
              style={{ zIndex: 1000, minWidth: '150px', padding: '10px 20px' }}
            />}
          </CardBody>
        </Card>
       : 
        <Grid
          {...dataState}
          data={process(filteredNotifications, dataState)}
          onDataStateChange={e => setDataState(e.dataState)}
          pageable={true}
          sortable={true}
          className='flo-notification-grid'
        >
          <GridColumn field="notificationSubject" title="Subject" />
          <GridColumn field="toCount" title="To" 
            cell={props =>  
              <td onClick={() => handleStatClick(props.dataItem)} {...props.tdProps} style={{ color: 'blue', backgroundColor: 'transparent', cursor: 'pointer', textDecoration: 'underline' }} >
                {props.dataItem.toCount}
              </td>
              }
          />
          <GridColumn 
            field="sentBy" 
            title="Sent By"
          />
          <GridColumn 
            field="dateSent" 
            title="Time Sent"
            cell={props =>  
              <td>
                {props.dataItem.dateSent ? new Date(props.dataItem.dateSent).toLocaleString() : ''}
              </td>
            }
          />
        </Grid>}
      {showDialog &&
        <Dialog
          {...dialogProps}
          onClose={() => setShowDialog(false)}
        />}   
      {showListView &&
        <NotificationListView
          showNotifications={showNotifications}
          dataItem={targetNotification}
          cancelEdit={() => {setShowListView(false);}}
        />
      }   
    </>
  )
}

const styles = {
  card: {
    minWidth: 800,
    margin: 5,
  },
  cardHeader: {
    flexDirection: 'column',
    minHeight: '140px'
  },
  cardTitle: {
    paddingTop: 10
  },
  cardSubtitle: {
    maxHeight: 100,
    marginTop: 5
  },
  aboutSection: {
    marginBottom: 20
  },
  aboutSectionTitle: {
    fontWeight: 'bold',
    fontSize: 15,
  },
  aboutSectionContent: {
    paddingTop: 5,
    whiteSpace: 'pre-line',
  },
  termsModalContent: {
    whiteSpace: 'pre-line',
  },
}

export default DashboardNotifications;